import { Injectable } from '@angular/core'
import { ApiClient, ApiClientFactory } from '@ftr/api-shared'
import { Department } from '@ftr/contracts/api/department'
import { JobWithTranscriptResponse } from '@ftr/contracts/api/job'
import { LogSheet } from '@ftr/contracts/api/log-sheet'
import { AudioAvailabilityResult, RecordingSegment, RecordingSummary } from '@ftr/contracts/read'
import { sessionToLocalTimeRange } from '@ftr/contracts/shared/SessionDateTimeRangeUtils'
import { RecordingType } from '@ftr/contracts/type/recording'
import { generateUuid, LocalTimeRange, Money, Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { plainToClass } from '@ftr/serialization'
import { FileUpload, UploadStates } from '@ftr/ui-files'
import { RecordingSummaryWithTimeRange, TimeRangeService } from '@ftr/ui-playback'
import { filter, map, Observable } from 'rxjs'
import {
  TranscriptFileUploadService,
  TranscriptUploadProps,
} from '../../lib/transcript-upload/transcript-file-upload.service'

// Recordings without departments will be grouped under 'undefined'
export type RecordingsByDepartment = Map<
  Uuid | undefined,
  {
    department: Department | undefined
    recordings: RecordingSummaryWithTimeRange[]
  }
>

@Injectable()
export class JobService {
  private readonly apiClient: ApiClient

  constructor(
    apiClientFactory: ApiClientFactory,
    private readonly transcriptFileUploadService: TranscriptFileUploadService,
    private readonly timeRangeService: TimeRangeService,
  ) {
    this.apiClient = apiClientFactory.build('/job')
  }

  getWithTranscriptFile(jobId: Uuid): ApiResult<JobWithTranscriptResponse> {
    return this.apiClient.get<JobWithTranscriptResponse>({
      path: `/${jobId}/getWithTranscriptFile`,
      responseBodyType: JobWithTranscriptResponse,
    })
  }

  getTranscriptionBundleLogSheets(jobId: Uuid): ApiResult<LogSheet[]> {
    return this.apiClient.get<LogSheet[]>({
      path: `/${jobId}/getTranscriptionBundleLogSheets`,
    })
  }

  isAudioAvailable(jobId: Uuid): ApiResult<AudioAvailabilityResult> {
    return this.apiClient.get<AudioAvailabilityResult>({
      path: `/${jobId}/isAudioAvailable`,
    })
  }

  submitTranscript(
    jobId: Uuid,
    file: File,
    transcriberFee: Money,
    fileId = generateUuid(),
  ): Observable<JobWithTranscriptResponse> {
    const upload: FileUpload<TranscriptUploadProps, JobWithTranscriptResponse> = {
      id: fileId,
      file,
      status: UploadStates.Initializing,
      props: {
        fileId,
        jobId,
        transcriberFee,
      },
    }

    return this.transcriptFileUploadService.upload(upload).pipe(
      filter(fileUpload => fileUpload.status === UploadStates.Success),
      map(fileUpload => fileUpload.response!),
    )
  }

  listRecordings(jobId: Uuid): ApiResult<RecordingsByDepartment> {
    return this.apiClient
      .get<RecordingSummary, RecordingSummary[]>({
        path: `/${jobId}/listRecordings`,
      })
      .pipe(mapData(response => this.mapRecordingSummaries(plainToClass(RecordingSummary, response))))
  }

  attachRecordings(jobId: Uuid): ApiResult<JobWithTranscriptResponse> {
    return this.apiClient.post<JobWithTranscriptResponse>({
      path: `${jobId}/attachRecordings`,
    })
  }

  /**
   * It is safe to rely on courtroom sessions here – they are provided for all recording types.
   */
  private mapRecordingSummaries(recordingSummaries: RecordingSummary[]): RecordingsByDepartment {
    // Make sure the "no department" group is always first
    const recordingsByDepartmentMap = new Map()
    recordingsByDepartmentMap.set(undefined, {
      department: undefined,
      recordings: [],
    })

    for (const courtroomSession of recordingSummaries.flatMap(s => s.courtroomSessions)) {
      if (!recordingsByDepartmentMap.has(courtroomSession.departmentId)) {
        recordingsByDepartmentMap.set(courtroomSession.departmentId, {
          department: courtroomSession.department,
          recordings: [],
        })
      }

      const recording = recordingSummaries.find(s => s.id === courtroomSession.recordingId)
      if (recording) {
        recordingsByDepartmentMap.get(courtroomSession.departmentId).recordings.push({
          recording,
          timeRange:
            recording.recordingType === RecordingType.TRM
              ? this.getRecordingTimeRange(recording.segments)
              : sessionToLocalTimeRange(courtroomSession),
        })
      }
    }

    // Delete the "no department" group if there are no recordings without a department
    if (recordingsByDepartmentMap.get(undefined).recordings.length === 0) {
      recordingsByDepartmentMap.delete(undefined)
    }

    return recordingsByDepartmentMap
  }

  private getRecordingTimeRange(segments: RecordingSegment[]): LocalTimeRange {
    const timeRanges = this.timeRangeService.convertToContiguousApproximateTimeRanges(segments)
    return new LocalTimeRange(timeRanges[0].start.toLocalTime(), timeRanges[timeRanges.length - 1].end.toLocalTime())
  }
}
